import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ShoppingCart, Search, Filter, Plus, ChevronLeft, ChevronRight,
  X, Eye, Pencil, Trash2, ChevronDown, ChevronUp, Calendar,
  Clock, AlertCircle, CheckCircle, Truck, Package, Download, Send, PackageCheck, FileDown, FileText,
  Copy
} from 'lucide-react';
import CreateButton from '../components/CreateButton';

const ITEMS_PER_PAGE = 50;

const IVA_MAP = { 1: 4, 2: 10, 3: 21 };

const getIvaDisplay = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return null;
  if (IVA_MAP[n]) return IVA_MAP[n];
  return n;
};

const ESTADO_MAP = {
  1: { label: 'Abierto', bg: 'bg-blue-100', text: 'text-blue-700' },
  2: { label: 'Confirmado', bg: 'bg-green-100', text: 'text-green-700' },
  3: { label: 'Enviado', bg: 'bg-purple-100', text: 'text-purple-700' },
  4: { label: 'Envío parcial', bg: 'bg-yellow-100', text: 'text-yellow-700' },
};

const TABS = [
  { key: 'hoy', label: 'Hoy', icon: Clock },
  { key: 'abiertos', label: 'Abiertos', icon: AlertCircle },
  { key: 'confirmados', label: 'Confirmados', icon: CheckCircle },
  { key: 'enviados', label: 'Enviados', icon: Send },
  { key: 'envio_parcial', label: 'Envío parcial', icon: PackageCheck },
  { key: 'centralsalud', label: 'CentralSalud', icon: Truck },
  { key: 'todos', label: 'Todos', icon: Package },
];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' €';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const OrderManagement = ({ defaultFilter }) => {
  const { token, API_URL } = useAuth();
  const navigate = useNavigate();

  // Data state
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [activeTab, setActiveTab] = useState(defaultFilter || 'todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [page, setPage] = useState(1);

  // UI state
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedItems, setExpandedItems] = useState([]);
  const [expandedComments, setExpandedComments] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [showEstadoModal, setShowEstadoModal] = useState(false);
  const [estadoChange, setEstadoChange] = useState({ orderId: null, newEstado: null });
  const [changingEstado, setChangingEstado] = useState(false);
  const [repeatingOrder, setRepeatingOrder] = useState(null);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/orders/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar estadísticas');
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [API_URL, token]);

  // Fetch orders
  const fetchOrders = useCallback(async (currentPage = 1, search = '', tab = activeTab, desde = fechaDesde, hasta = fechaHasta, estado = estadoFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: ITEMS_PER_PAGE });
      if (tab && tab !== 'todos') params.append('filter', tab);
      if (search.trim()) params.append('search', search.trim());
      if (desde) params.append('desde', desde);
      if (hasta) params.append('hasta', hasta);
      if (estado) params.append('estado', estado);

      const res = await fetch(`${API_URL}/orders?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar pedidos');
      const data = await res.json();
      setOrders(data.orders || []);
      setTotalOrders(data.pagination?.total || data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, activeTab, fechaDesde, fechaHasta, estadoFilter]);

  // Initial load
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Fetch on tab/page/filter change
  useEffect(() => {
    fetchOrders(page, searchTerm, activeTab, fechaDesde, fechaHasta, estadoFilter);
  }, [page, activeTab, fechaDesde, fechaHasta, estadoFilter]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setPage(1);
      fetchOrders(1, searchTerm, activeTab, fechaDesde, fechaHasta, estadoFilter);
    }, 400);
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Tab change handler
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setPage(1);
    setExpandedRow(null);
    setExpandedItems([]);
  };

  // Expand row to show line items
  const handleRowExpand = async (orderId) => {
    if (expandedRow === orderId) {
      setExpandedRow(null);
      setExpandedItems([]);
      setExpandedComments([]);
      return;
    }
    setExpandedRow(orderId);
    setLoadingItems(true);
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar líneas');
      const data = await res.json();
      setExpandedItems(data.order?.lineas || data.items || data.lineas || []);
      setExpandedComments(data.order?.comentarios || data.comentarios || []);
    } catch (err) {
      setExpandedItems([]);
      setExpandedComments([]);
      console.error('Error fetching order items:', err);
    } finally {
      setLoadingItems(false);
    }
  };

  // CSV download handler
  const handleCsvDownload = async (orderId, embalajeParam) => {
    setDownloadingCsv(true);
    try {
      const url = embalajeParam !== undefined
        ? `${API_URL}/orders/${orderId}/csv?embalaje=${embalajeParam}`
        : `${API_URL}/orders/${orderId}/csv`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al descargar CSV');
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `pedido_${orderId}.csv`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      // Refresh order list (estado may have changed to 3)
      fetchOrders(page, searchTerm, activeTab, fechaDesde, fechaHasta, estadoFilter);
      fetchStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloadingCsv(false);
    }
  };

  // PDF download handler
  const handlePdfDownload = async (orderId) => {
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al descargar PDF');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `pedido_${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      setError(err.message);
    }
  };

  // Check if order has embalaje issues
  const hasEmbalajeIssues = (items) => {
    return items.some(item =>
      item.cantidad_embalaje && item.cantidad_embalaje > 1 &&
      item.cantidad % item.cantidad_embalaje !== 0
    );
  };

  // Estado change
  const handleEstadoChangeRequest = (orderId, newEstado) => {
    setEstadoChange({ orderId, newEstado });
    setShowEstadoModal(true);
  };

  const confirmEstadoChange = async () => {
    setChangingEstado(true);
    try {
      const res = await fetch(`${API_URL}/orders/${estadoChange.orderId}/estado`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ estado: estadoChange.newEstado })
      });
      if (!res.ok) throw new Error('Error al cambiar estado');
      setShowEstadoModal(false);
      setEstadoChange({ orderId: null, newEstado: null });
      fetchOrders(page, searchTerm, activeTab, fechaDesde, fechaHasta, estadoFilter);
      fetchStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setChangingEstado(false);
    }
  };

  // Delete order
  const confirmDelete = (order) => {
    setDeletingOrder(order);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deletingOrder) return;
    try {
      const res = await fetch(`${API_URL}/orders/${deletingOrder.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al eliminar pedido');
      setShowDeleteModal(false);
      setDeletingOrder(null);
      fetchOrders(page, searchTerm, activeTab, fechaDesde, fechaHasta, estadoFilter);
      fetchStats();
    } catch (err) {
      setError(err.message);
    }
  };

  // Repeat order
  const handleRepeatOrder = async (orderId) => {
    setRepeatingOrder(orderId);
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}/repetir`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Error al repetir pedido');
      }
      const data = await res.json();
      navigate(`/pedidos/nuevo?order=${data.targetOrderId}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setRepeatingOrder(null);
    }
  };

  const totalPages = Math.ceil(totalOrders / ITEMS_PER_PAGE);

  const getStatCount = (key) => {
    return stats['pedidos_' + key] ?? stats[key] ?? 0;
  };

  const canEdit = (order) => !order.albaranes || order.albaranes.length === 0;
  const canDelete = (order) => !order.albaranes || order.albaranes.length === 0;

  return (
    <div className="h-full flex flex-col overflow-hidden">

      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
              <ShoppingCart className="w-6 h-6" style={{ color: '#0099FF' }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Gestión de Pedidos</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                  Hoy: {getStatCount('hoy')}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700">
                  Abiertos: {getStatCount('abiertos')}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                  Confirmados: {getStatCount('confirmados')}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                  CentralSalud: {getStatCount('centralsalud')}
                </span>
              </div>
            </div>
          </div>
          <CreateButton href="/pedidos/nuevo">Nuevo Pedido</CreateButton>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mt-4 flex items-center justify-between">
            <span className="text-sm">{error}</span>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X size={16} /></button>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="bg-white border-b border-gray-200 px-6 flex-shrink-0">
        <div className="flex items-center gap-1 -mb-px overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            const count = tab.key !== 'todos' ? getStatCount(tab.key) : null;
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className="flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
                style={isActive
                  ? { borderColor: '#0099FF', color: '#0077CC' }
                  : { borderColor: 'transparent', color: '#6b7280' }
                }
              >
                <Icon size={14} />
                {tab.label}
                {count !== null && (
                  <span
                    className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-semibold"
                    style={isActive
                      ? { backgroundColor: 'rgba(0,153,255,0.08)', color: '#0077CC' }
                      : { backgroundColor: '#f3f4f6', color: '#6b7280' }
                    }
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nº pedido o cliente..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Date range */}
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-gray-400" />
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => { setFechaDesde(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Desde"
            />
            <span className="text-gray-400 text-sm">—</span>
            <input
              type="date"
              value={fechaHasta}
              onChange={(e) => { setFechaHasta(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Hasta"
            />
          </div>

          {/* Estado dropdown */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={estadoFilter}
              onChange={(e) => { setEstadoFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los estados</option>
              <option value="1">Abierto</option>
              <option value="2">Confirmado</option>
              <option value="3">Enviado</option>
              <option value="4">Envío Parcial</option>
            </select>
          </div>

          <span className="text-xs text-gray-400 ml-auto">{totalOrders} pedidos</span>
        </div>
      </div>

      {/* Table area (scrollable) */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nº Pedido</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Líneas</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Albarán</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading && orders.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: '#0099FF' }}></div>
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center">
                      <ShoppingCart size={36} className="mx-auto mb-3 text-gray-300" />
                      <p className="text-sm text-gray-500">No se encontraron pedidos</p>
                      <p className="text-xs text-gray-400 mt-1">Intente con otros criterios de búsqueda</p>
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <React.Fragment key={order.id}>
                      {/* Main row */}
                      <tr
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleRowExpand(order.id)}
                      >
                        <td className="px-4 py-3 text-center">
                          {expandedRow === order.id
                            ? <ChevronUp size={14} className="text-gray-400" />
                            : <ChevronDown size={14} className="text-gray-400" />
                          }
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-500">
                          {order.id}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium" style={{ color: '#0077CC' }}>
                          {order.npedido || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 max-w-[200px] truncate">
                          {order.cliente_nombre || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {order.usuario_nombre || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(order.fecha)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={order.estado}
                            onChange={(e) => handleEstadoChangeRequest(order.id, parseInt(e.target.value))}
                            className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              ESTADO_MAP[order.estado]?.bg || 'bg-gray-100'
                            } ${ESTADO_MAP[order.estado]?.text || 'text-gray-600'}`}
                          >
                            <option value={1}>Abierto</option>
                            <option value={2}>Confirmado</option>
                            <option value={3}>Enviado</option>
                            <option value={4}>Envío Parcial</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                          {order.num_lineas ?? order.lineas ?? '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          {formatCurrency(order.itotaliva)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center" onClick={(e) => e.stopPropagation()}>
                          {order.albaranes && order.albaranes.length > 0 ? (
                            <div className="flex items-center justify-center gap-1 flex-wrap">
                              {order.albaranes.map((alb) => {
                                const albaranId = String(alb.id);
                                const expedition = albaranId.split('.')[0];
                                const pdfUrl = `https://comprassanitarias.com/albaranes/${albaranId}`;
                                const trackingUrl = `${API_URL}/orders/tracking/${albaranId}`;
                                return (
                                  <span key={alb.id} className="inline-flex items-center gap-0.5">
                                    <a
                                      href={pdfUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1 rounded text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                                      title={`Albarán ${albaranId}`}
                                    >
                                      <FileText size={15} />
                                    </a>
                                    <a
                                      href={trackingUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1 rounded text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 transition-colors"
                                      title={`Seguimiento envío ${expedition}`}
                                    >
                                      <Truck size={15} />
                                    </a>
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="text-gray-400">S/N</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => navigate(`/pedidos/nuevo?order=${order.id}&readonly=1`)}
                              className="p-1.5 rounded text-gray-400 hover:text-blue-600 transition-colors"
                              title="Ver"
                            >
                              <Eye size={15} />
                            </button>
                            <button
                              onClick={() => handlePdfDownload(order.id)}
                              className="p-1.5 rounded text-gray-400 hover:text-blue-600 transition-colors"
                              title="Descargar PDF"
                            >
                              <FileDown size={15} />
                            </button>
                            <button
                              onClick={() => handleRepeatOrder(order.id)}
                              disabled={repeatingOrder === order.id}
                              className="p-1.5 rounded text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
                              title="Repetir pedido"
                            >
                              {repeatingOrder === order.id ? (
                                <div className="w-[15px] h-[15px] border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Copy size={15} />
                              )}
                            </button>
                            {canEdit(order) && (
                              <button
                                onClick={() => navigate(`/pedidos/nuevo?order=${order.id}`)}
                                className="p-1.5 rounded text-gray-400 hover:text-blue-600 transition-colors"
                                title="Editar"
                              >
                                <Pencil size={15} />
                              </button>
                            )}
                            {canDelete(order) && (
                              <button
                                onClick={() => confirmDelete(order)}
                                className="p-1.5 rounded text-red-600 hover:text-red-400 transition-colors duration-200"
                                title="Eliminar"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded line items */}
                      {expandedRow === order.id && (
                        <tr>
                          <td colSpan="11" className="px-0 py-0">
                            <div className="px-8 py-4 border-t border-b" style={{ backgroundColor: 'rgba(0,153,255,0.04)', borderColor: 'rgba(0,153,255,0.1)' }}>
                              {loadingItems ? (
                                <div className="flex items-center justify-center py-4">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: '#0099FF' }}></div>
                                  <span className="ml-2 text-sm text-gray-500">Cargando líneas...</span>
                                </div>
                              ) : expandedItems.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-3">Sin líneas de pedido</p>
                              ) : (
                                <>
                                  <table className="min-w-full">
                                    <thead>
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ref</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Precio</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">IVA</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {expandedItems.map((item, idx) => (
                                        <tr key={item.id || idx} className="border-t border-gray-200/50">
                                          <td className="px-4 py-2 text-sm font-mono text-gray-500">{item.raiz || item.referencia || '—'}</td>
                                          <td className="px-4 py-2 text-sm text-gray-800">{item.nombre || item.producto || '—'}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right">{item.cantidad ?? '—'}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right">{formatCurrency(item.precio)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 text-right">{(() => { const v = getIvaDisplay(item.tipo_iva) || getIvaDisplay(item.iva); return v ? `${v}%` : '—'; })()}</td>
                                          <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">{formatCurrency(item.subtotal || (item.cantidad * item.precio))}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>

                                  {/* Comments */}
                                  {expandedComments.length > 0 && (
                                    <div className="mt-4 pt-3 border-t border-gray-200/50">
                                      <p className="text-xs font-medium text-gray-500 uppercase mb-2">Comentarios</p>
                                      <div className="space-y-2">
                                        {expandedComments.map((comment) => (
                                          <div key={comment.id} className="bg-white rounded px-3 py-2 text-sm border border-gray-200/50">
                                            <span className="text-gray-800">{comment.comentario || comment.texto}</span>
                                            <span className="text-xs text-gray-400 ml-2">
                                              — {comment.usuario_nombre || 'Sistema'}, {formatDate(comment.fecha)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Action buttons */}
                                  <div className="mt-4 pt-3 border-t border-gray-200/50 flex items-center gap-3">
                                    {/* CSV download for any non-open order (estado != 1) */}
                                    {order.estado !== 1 && (
                                      hasEmbalajeIssues(expandedItems) ? (
                                        <>
                                          <button
                                            onClick={() => handleCsvDownload(order.id, 0)}
                                            disabled={downloadingCsv}
                                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                                          >
                                            <Download size={14} />
                                            {downloadingCsv ? 'Descargando...' : 'Descargar Original'}
                                          </button>
                                          <button
                                            onClick={() => handleCsvDownload(order.id, 1)}
                                            disabled={downloadingCsv}
                                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50"
                                            style={{ backgroundColor: '#0099FF' }}
                                          >
                                            <Download size={14} />
                                            {downloadingCsv ? 'Descargando...' : 'Descargar con Embalaje'}
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          onClick={() => handleCsvDownload(order.id)}
                                          disabled={downloadingCsv}
                                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white transition-colors disabled:opacity-50"
                                          style={{ backgroundColor: '#0099FF' }}
                                        >
                                          <Download size={14} />
                                          {downloadingCsv ? 'Descargando...' : 'Descargar CSV'}
                                        </button>
                                      )
                                    )}

                                    {/* Edit button for orders without albarán */}
                                    {canEdit(order) && (
                                      <a
                                        href={`/pedidos/nuevo?order=${order.id}&cliente=${order.cliente}`}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white transition-colors"
                                        style={{ backgroundColor: '#0099FF' }}
                                      >
                                        <Pencil size={14} />
                                        Editar
                                      </a>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white border-t border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Mostrando {((page - 1) * ITEMS_PER_PAGE) + 1} a {Math.min(page * ITEMS_PER_PAGE, totalOrders)} de {totalOrders} pedidos
            </p>
            <div className="flex space-x-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                <ChevronLeft size={14} /> Anterior
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                      page === pageNum
                        ? 'text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                    style={page === pageNum ? { backgroundColor: '#0099FF' } : {}}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                Siguiente <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estado Change Confirmation Modal */}
      {showEstadoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4">
            <div className="px-6 py-5">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
                  <ShoppingCart size={20} style={{ color: '#0099FF' }} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirmar cambio de estado</h3>
              </div>
              <p className="text-sm text-gray-600">
                ¿Confirmar cambio de estado a <strong>{ESTADO_MAP[estadoChange.newEstado]?.label || 'Desconocido'}</strong>?
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => { setShowEstadoModal(false); setEstadoChange({ orderId: null, newEstado: null }); }}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmEstadoChange}
                disabled={changingEstado}
                className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#0099FF' }}
              >
                {changingEstado && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4">
            <div className="px-6 py-5">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 rounded-full bg-red-50">
                  <Trash2 size={20} className="text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirmar eliminación</h3>
              </div>
              <p className="text-sm text-gray-600">
                ¿Está seguro de que desea eliminar el pedido <strong>#{deletingOrder?.npedido || deletingOrder?.id}</strong>?
                Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => { setShowDeleteModal(false); setDeletingOrder(null); }}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 size={14} /> Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
